import { z } from "zod";
import { Persistence } from "../../config/constants";
import { TickLayerName } from "../../config/tickLayers";
import { BaseEntity } from "../../core/entity/BaseEntity";
import { Entity } from "../../core/entity/Entity";
import { on } from "../../core/entity/handler";
import { dayPhase, DayPhase, MINUTES_PER_DAY } from "../../game/time";
import {
  registerSerializable,
  SerializableEntity,
} from "../save/serialization";
import { TimeFlow } from "../TimeFlow";

/**
 * The shop clock: what minute it is, which calendar day, and when this
 * morning started.
 *
 * `minute` is the shop's minute counter (see `src/game/time.ts` for the
 * day's units), and it is fractional: every engine tick it advances by
 * the shop minutes TimeFlow brought — the slice of a minute the pace
 * runs, or a forced batch served whole — so a save lands wherever the
 * clock stands.
 *
 * The day advances only by sleeping — the drive home sets a new
 * `dayStartMinute` and bumps `day` — never by the counter rolling over.
 */

const schema = z.object({
  minute: z.number(),
  day: z.number().int(),
  dayStartMinute: z.number(),
});

type ClockData = z.infer<typeof schema>;

export class Clock extends BaseEntity implements Entity, SerializableEntity {
  readonly saveType = "clock";
  id = "clock";
  tickLayer: TickLayerName = "clock";
  persistenceLevel: number = Persistence.Game;

  minute: number;
  day: number;
  dayStartMinute: number;

  constructor(data: Partial<ClockData> = {}) {
    super();
    this.minute = data.minute ?? 0;
    this.day = data.day ?? 1;
    this.dayStartMinute = data.dayStartMinute ?? 0;
  }

  /** How many of today's working minutes have been spent. */
  dayMinutesSpent(): number {
    return this.minute - this.dayStartMinute;
  }

  /**
   * The same question asked of a particular minute. A sim layer that
   * runs many minutes in one engine tick has already watched the counter
   * jump to the end of the batch, so it asks about the minute it is
   * simulating rather than the one the clock now reads.
   */
  isNightAt(minute: number): boolean {
    return minute - this.dayStartMinute >= MINUTES_PER_DAY;
  }

  /** Where today stands, morning through night. */
  currentDayPhase(): DayPhase {
    return dayPhase(this.dayMinutesSpent());
  }

  /**
   * Whether the shop is closed for the night: the day's budget is spent.
   * Nothing new starts and idle time stops passing; only work already
   * running (or the drive home) moves the world.
   */
  isNight(): boolean {
    return this.isNightAt(this.minute);
  }

  @on("afterAdded")
  onAfterAdded() {
    // The clock is what knows night; TimeFlow asks it when resolving pace.
    const timeFlow = this.game.entities.tryGetSingleton(TimeFlow);
    timeFlow?.setNightProvider(() => this.isNight());
  }

  @on("tick")
  onTick() {
    const timeFlow = this.game.entities.tryGetSingleton(TimeFlow);
    if (timeFlow) {
      this.minute += timeFlow.gameDt;
    }
  }

  toJSON(): ClockData {
    return {
      minute: this.minute,
      day: this.day,
      dayStartMinute: this.dayStartMinute,
    };
  }
}

registerSerializable({
  type: "clock",
  singleton: true,
  schema,
  fromJSON: (data) => new Clock(data),
});
